/**
 * WaitlistModal — reusable in-app email capture for all three waitlist types.
 */
import { useState } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, Modal,
  StyleSheet, ActivityIndicator, KeyboardAvoidingView, Platform,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { joinWaitlist, WAITLIST_CONFIGS, type WaitlistType } from '@/lib/waitlist';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';
import { TYPO } from '@/constants/typography';
import { X, CheckCircle } from 'lucide-react-native';

interface Props {
  visible: boolean;
  type: WaitlistType;
  onClose: () => void;
}

export default function WaitlistModal({ visible, type, onClose }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;
  const config = WAITLIST_CONFIGS[type];

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    const result = await joinWaitlist({ type, email, name, notes });
    setLoading(false);
    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error || 'Something went wrong.');
    }
  };

  const handleClose = () => {
    setEmail('');
    setName('');
    setNotes('');
    setError('');
    setSuccess(false);
    onClose();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.overlay}
      >
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>
              {config.title}
            </Text>
            <TouchableOpacity onPress={handleClose} hitSlop={12} accessibilityRole="button" accessibilityLabel="Close">
              <X size={20} color={c.textSecondary} />
            </TouchableOpacity>
          </View>

          {success ? (
            <View style={styles.successContainer}>
              <CheckCircle size={40} color={c.accent} />
              <Text style={[styles.successText, { color: c.text }]}>{config.successMessage}</Text>
              <TouchableOpacity
                style={[styles.btn, { backgroundColor: c.accent, marginTop: SPACE.lg }]}
                onPress={handleClose}
              >
                <Text style={[styles.btnText, { color: c.accentForeground }]}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              <Text style={[styles.subtitle, { color: c.textSecondary }]}>{config.subtitle}</Text>

              <View style={styles.form}>
                <TextInput
                  style={[styles.input, { backgroundColor: c.surfaceVariant, borderColor: c.border, color: c.text }]}
                  placeholder={config.namePlaceholder}
                  placeholderTextColor={c.textTertiary}
                  value={name}
                  onChangeText={setName}
                  autoCapitalize="words"
                  returnKeyType="next"
                />
                <TextInput
                  style={[styles.input, { backgroundColor: c.surfaceVariant, borderColor: c.border, color: c.text }]}
                  placeholder={config.placeholder}
                  placeholderTextColor={c.textTertiary}
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  returnKeyType="next"
                />
                <Text style={[styles.label, { color: c.textSecondary }]}>{config.notesLabel}</Text>
                <TextInput
                  style={[styles.input, styles.notesInput, { backgroundColor: c.surfaceVariant, borderColor: c.border, color: c.text }]}
                  placeholder={config.notesPlaceholder}
                  placeholderTextColor={c.textTertiary}
                  value={notes}
                  onChangeText={setNotes}
                  multiline
                  numberOfLines={3}
                  returnKeyType="done"
                />
              </View>

              {error ? (
                <Text style={[styles.error, { color: '#ef4444' }]}>{error}</Text>
              ) : null}

              <TouchableOpacity
                style={[styles.btn, { backgroundColor: c.accent, opacity: loading ? 0.7 : 1 }]}
                onPress={handleSubmit}
                disabled={loading}
                accessibilityRole="button"
              >
                {loading ? (
                  <ActivityIndicator color={c.accentForeground} size="small" />
                ) : (
                  <Text style={[styles.btnText, { color: c.accentForeground }]}>{config.ctaLabel}</Text>
                )}
              </TouchableOpacity>
            </>
          )}
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  card: {
    width: '100%',
    borderRadius: RADIUS.xs,
    borderWidth: 1,
    padding: 24,
    gap: SPACE.md,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontSize: FONT.lg,
    flex: 1,
    marginRight: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  subtitle: {
    fontSize: FONT.sm,
    lineHeight: 20,
  },
  form: {
    gap: 10,
  },
  label: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    marginTop: 4,
  },
  input: {
    borderWidth: 1,
    borderRadius: RADIUS.xs,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: FONT.base,
  },
  notesInput: {
    height: 80,
    textAlignVertical: 'top',
    paddingTop: 12,
  },
  error: {
    fontSize: 13,
    textAlign: 'center',
  },
  btn: {
    paddingVertical: 16,
    borderRadius: 0,
    alignItems: 'center',
  },
  btnText: {
    fontSize: 15,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  successContainer: {
    alignItems: 'center',
    gap: 12,
    paddingVertical: 8,
  },
  successText: {
    fontSize: FONT.base,
    textAlign: 'center',
    lineHeight: 22,
  },
});
